import { ExamPeriodMonitor, ExamPeriodMonitorState } from "@types"
import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { setTimeWithSeconds } from "../../helpers";
import { setInterval, clearInterval } from 'worker-timers'
import {
  anchorMonotonic,
  remainingSecondsMonotonic,
  fallbackSnapshot,
  ServerTimerSnapshot,
  MonotonicAnchor,
} from "./examTimer";
import { useClockOffset } from "../../../hooks/useClockOffset";

interface Props {
    examMonitor: ExamPeriodMonitor
}

/**
 * Prikaz preostalog vremena na monitoringu - NAJROBUSNIJI pristup, identičan
 * playeru: izvor istine je autoritativni serverski snapshot (serverNow + endsAt +
 * state) iz `ComputeExamInstanceSnapshotAsync`, uz procijenjeni offset sata
 * (Cristian) i odbrojavanje MONOTONIM satom (performance.now), imunim na drift
 * intervala i skokove sistemskog sata.
 *
 * Dok je pauza (snapshot.isRunning=false) prikaz se zamrzava na serverskoj
 * vrijednosti - kao i kod polaznika.
 */
export const TableTimeLeft = ({examMonitor}: Props) => {
    const [time, setTime] = useState<dayjs.Dayjs | undefined>(undefined);
    const anchorRef = useRef<MonotonicAnchor | null>(null);
    const clockOffsetMs = useClockOffset();

    const isVisible =
        examMonitor.state === ExamPeriodMonitorState.InProgress ||
        examMonitor.state === ExamPeriodMonitorState.Paused;

    // (Re)sidri monotoni countdown iz svježeg serverskog snapshota (refetch svakih 15 s).
    useEffect(() => {
        if (!isVisible) {
            anchorRef.current = null;
            setTime(undefined);
            return;
        }

        // Fallback ako stariji backend ne vrati `sync`: izgradi snapshot iz instanceTimer.
        const snap: ServerTimerSnapshot = examMonitor.sync ?? fallbackSnapshot(
            examMonitor.id,
            examMonitor.instanceTimer ?? null,
            examMonitor.state === ExamPeriodMonitorState.InProgress,
            examMonitor.state
        );

        const anchor = anchorMonotonic(snap, clockOffsetMs);
        anchorRef.current = anchor;
        if (anchor) {
            setTime(setTimeWithSeconds(remainingSecondsMonotonic(anchor)));
        } else {
            setTime(undefined);
        }
    }, [examMonitor.id, examMonitor.sync, examMonitor.instanceTimer, examMonitor.state, isVisible, clockOffsetMs])

    // Odbrojavanje monotonim satom (otporno na drift intervala). Dok je pauza,
    // anchor.running=false pa prikaz ostaje zamrznut.
    useEffect(() => {
        const interval = setInterval(() => {
            const anchor = anchorRef.current;
            if (!anchor) {
                return;
            }
            const remainingSec = remainingSecondsMonotonic(anchor);
            setTime(setTimeWithSeconds(remainingSec));
            if (anchor.running && remainingSec <= 0) {
                clearInterval(interval);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [examMonitor.id])

    return (
        isVisible ?
        <span>{time?.format("HH:mm:ss")}</span> : <span>-</span>
    )
}